import { useState, useEffect, useCallback } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { Plus, Pencil, Trash2, Upload, Megaphone, BarChart3, Vote, Bell, Users, Eye, FileText, X, Send, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_OPTIONS = [
  { value: 'duyuru', label: 'Duyuru', icon: Megaphone, color: 'bg-blue-100 text-blue-700', desc: 'Bilgilendirme amaçlı' },
  { value: 'anket', label: 'Görüş Toplama', icon: BarChart3, color: 'bg-amber-100 text-amber-700', desc: 'Bağlayıcı olmayan yoklama' },
  { value: 'oylama', label: 'Karar Oylaması', icon: Vote, color: 'bg-green-100 text-green-700', desc: 'Resmi karar mekanizması' },
];

const VOTE_TYPE_OPTIONS = [
  { value: 'evet_hayir', label: 'Evet / Hayır / Çekimser' },
  { value: 'coktan_secmeli', label: 'Çoktan Seçmeli' },
];

const VISIBILITY_OPTIONS = [
  { value: 'tum_sakinler', label: 'Tüm Sakinler' },
  { value: 'mal_sahipleri', label: 'Sadece Mal Sahipleri' },
  { value: 'kiracilar', label: 'Sadece Kiracılar' },
];

const STATUS_LABELS = {
  aktif: { label: 'Aktif', color: 'bg-green-100 text-green-700' },
  kapali: { label: 'Kapalı', color: 'bg-gray-200 text-gray-600' },
};

const emptyForm = {
  title: '', description: '', announcement_type: 'duyuru',
  vote_type: '', options: ['', ''], deadline: '',
  target_visibility: 'tum_sakinler', result_sharing: true,
};

export const AdminAnnouncements = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  const [announcements, setAnnouncements] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [resultDialog, setResultDialog] = useState(null);
  const [resultData, setResultData] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const { data } = await axios.get(`${API_URL}/api/announcements?${params}`, { withCredentials: true });
      setAnnouncements(data);
    } catch (e) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [API_URL, filterType, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        options: formData.vote_type === 'coktan_secmeli'
          ? formData.options.filter(o => o.trim())
          : [],
      };
      if (formData.announcement_type === 'duyuru') {
        payload.vote_type = null;
        payload.deadline = null;
      }
      await axios.post(`${API_URL}/api/announcements`, payload, { withCredentials: true });
      toast.success('Duyuru oluşturuldu');
      setDialogOpen(false);
      setFormData(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu duyuruyu silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/announcements/${id}`, { withCredentials: true });
      toast.success('Duyuru silindi');
      fetchData();
    } catch (err) {
      toast.error('Silinemedi');
    }
  };

  const handleCloseVoting = async (id) => {
    try {
      await axios.put(`${API_URL}/api/announcements/${id}`, { status: 'kapali' }, { withCredentials: true });
      toast.success('Oylama kapatıldı');
      fetchData();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleRemind = async (id) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/announcements/${id}/remind`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hatırlatma gönderilemedi');
    }
  };

  const handleFileUpload = async (annId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/announcements/${annId}/upload`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Belge yüklendi');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Belge yüklenemedi');
    }
  };

  const showResults = async (id) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/announcements/${id}/results`, { withCredentials: true });
      setResultData(data);
      setResultDialog(id);
    } catch (err) {
      toast.error('Sonuçlar yüklenemedi');
    }
  };

  const downloadPdf = async (id, title) => {
    try {
      const response = await axios.get(`${API_URL}/api/announcements/${id}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `karar_tutanagi_${title.replace(/\s+/g, '_').substring(0, 30)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF indiriliyor');
    } catch (err) {
      toast.error('PDF indirilemedi');
    }
  };

  const addOption = () => setFormData(p => ({ ...p, options: [...p.options, ''] }));
  const removeOption = (idx) => setFormData(p => ({ ...p, options: p.options.filter((_, i) => i !== idx) }));
  const updateOption = (idx, val) => setFormData(p => ({ ...p, options: p.options.map((o, i) => i === idx ? val : o) }));

  const getTypeBadge = (type) => {
    const opt = TYPE_OPTIONS.find(t => t.value === type);
    return opt ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span> : type;
  };

  const getStatusBadge = (s) => {
    const opt = STATUS_LABELS[s];
    return opt ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span> : s;
  };

  const needsVoting = formData.announcement_type !== 'duyuru';

  if (loading) {
    return <PageLayout><div className="flex items-center justify-center min-h-[50vh]"><p className="text-[#595959]">Yükleniyor...</p></div></PageLayout>;
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" data-testid="announcements-title">
              Duyuru & Karar Destek
            </h1>
            <p className="text-sm text-[#595959] mt-1">Duyuru, anket ve oylama yönetimi</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setFormData(emptyForm); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#2563EB] hover:bg-[#1E4ED8] gap-2" data-testid="add-announcement-btn">
                <Plus className="w-4 h-4" /> Yeni Oluştur
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Yeni Duyuru / Oylama</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="announcement-form">
                {/* Type Selection */}
                <div className="space-y-1.5">
                  <Label>Tip *</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {TYPE_OPTIONS.map((t) => {
                      const Icon = t.icon;
                      const active = formData.announcement_type === t.value;
                      return (
                        <button
                          key={t.value} type="button"
                          onClick={() => setFormData(p => ({ ...p, announcement_type: t.value, vote_type: t.value === 'duyuru' ? '' : p.vote_type || 'evet_hayir' }))}
                          className={`p-3 rounded-xl border-2 transition-all text-center ${active ? `${t.color} border-transparent shadow` : 'bg-white border-[#E5E5E5] hover:border-[#2563EB]/30'}`}
                          data-testid={`type-${t.value}`}
                        >
                          <Icon className="w-5 h-5 mx-auto mb-1" />
                          <p className="text-xs font-medium">{t.label}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Başlık *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} required placeholder="Duyuru başlığı" data-testid="ann-title-input" />
                </div>

                <div className="space-y-1.5">
                  <Label>Açıklama *</Label>
                  <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[100px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} required placeholder="Detaylı açıklama..." data-testid="ann-description-input" />
                </div>

                {/* Voting options */}
                {needsVoting && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Oy Tipi</Label>
                        <Select value={formData.vote_type} onValueChange={(v) => setFormData(p => ({ ...p, vote_type: v }))}>
                          <SelectTrigger data-testid="vote-type-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                          <SelectContent>
                            {VOTE_TYPE_OPTIONS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Son Oy Tarihi *</Label>
                        <Input type="date" value={formData.deadline} onChange={(e) => setFormData(p => ({ ...p, deadline: e.target.value }))} required data-testid="ann-deadline-input" />
                      </div>
                    </div>

                    {formData.vote_type === 'coktan_secmeli' && (
                      <div className="space-y-2">
                        <Label>Seçenekler</Label>
                        {formData.options.map((opt, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input value={opt} onChange={(e) => updateOption(idx, e.target.value)} placeholder={`Seçenek ${idx + 1}`} data-testid={`option-${idx}`} />
                            {formData.options.length > 2 && (
                              <button type="button" onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                            )}
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={addOption} className="gap-1"><Plus className="w-3 h-3" /> Seçenek Ekle</Button>
                      </div>
                    )}
                  </>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Hedef Kitle</Label>
                    <Select value={formData.target_visibility} onValueChange={(v) => setFormData(p => ({ ...p, target_visibility: v }))}>
                      <SelectTrigger data-testid="ann-visibility-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {VISIBILITY_OPTIONS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Sonuç Paylaşımı</Label>
                    <Select value={formData.result_sharing ? 'acik' : 'kapali'} onValueChange={(v) => setFormData(p => ({ ...p, result_sharing: v === 'acik' }))}>
                      <SelectTrigger data-testid="ann-result-sharing"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="acik">Sonuçlar Açık</SelectItem>
                        <SelectItem value="kapali">Sonuçlar Gizli</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="ann-submit-btn">
                  Oluştur
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[170px]" data-testid="filter-ann-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Tipler</SelectItem>
              {TYPE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="filter-ann-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durum</SelectItem>
              <SelectItem value="aktif">Aktif</SelectItem>
              <SelectItem value="kapali">Kapalı</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Announcement List */}
        <div className="space-y-3">
          {announcements.length === 0 ? (
            <Card className="border border-[#E5E5E5]">
              <CardContent className="py-12 text-center">
                <Megaphone className="w-10 h-10 text-[#D0D0D0] mx-auto mb-3" />
                <p className="text-[#595959]">Henüz duyuru bulunmuyor</p>
              </CardContent>
            </Card>
          ) : announcements.map((ann) => {
            const isExpanded = expandedId === ann.id;
            const isVotable = ann.announcement_type !== 'duyuru';
            return (
              <Card key={ann.id} className="border border-[#E5E5E5]" data-testid={`ann-card-${ann.id}`}>
                <CardContent className="p-4 sm:p-5">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {getTypeBadge(ann.announcement_type)}
                        {getStatusBadge(ann.status)}
                        {ann.deadline && <span className="text-xs text-[#595959]">Son: {ann.deadline}</span>}
                      </div>
                      <h3 className="font-semibold text-[#1A1A2E] text-base sm:text-lg">{ann.title}</h3>
                    </div>
                    <button onClick={() => setExpandedId(isExpanded ? null : ann.id)} className="text-[#595959] hover:text-[#1A1A2E] shrink-0">
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-4 text-xs text-[#595959] mb-2">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {ann.view_count} görüntüleme</span>
                    {isVotable && <span className="flex items-center gap-1"><Vote className="w-3 h-3" /> {ann.total_votes} oy</span>}
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {VISIBILITY_OPTIONS.find(v => v.value === ann.target_visibility)?.label}</span>
                  </div>

                  {/* Vote Results Preview */}
                  {isVotable && ann.results && ann.vote_type === 'evet_hayir' && (
                    <div className="flex items-center gap-2 mb-2">
                      {['evet', 'hayir', 'cekimser'].map(k => (
                        <span key={k} className={`text-xs px-2 py-0.5 rounded-full ${k === 'evet' ? 'bg-green-100 text-green-700' : k === 'hayir' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                          {k === 'evet' ? 'Evet' : k === 'hayir' ? 'Hayır' : 'Çekimser'}: {ann.results[k] || 0}
                        </span>
                      ))}
                    </div>
                  )}

                  {isVotable && ann.results && ann.vote_type === 'coktan_secmeli' && (
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      {Object.entries(ann.results).map(([opt, count]) => (
                        <span key={opt} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                          {opt}: {count}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                      <p className="text-sm text-[#595959] whitespace-pre-wrap mb-3">{ann.description}</p>

                      {/* Documents */}
                      {ann.documents.length > 0 && (
                        <div className="space-y-1 mb-3">
                          <p className="text-xs font-medium text-[#595959]">Belgeler:</p>
                          {ann.documents.map(doc => (
                            <a key={doc} href={`${API_URL}/api/announcements/files/${ann.business_id}/${doc}`} target="_blank" rel="noreferrer"
                              className="flex items-center gap-2 text-sm text-[#2563EB] hover:underline bg-[#F9F8F6] px-3 py-1.5 rounded">
                              <FileText className="w-4 h-4" /> <span className="truncate">{doc}</span>
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {isVotable && ann.status === 'aktif' && (
                          <>
                            <Button variant="outline" size="sm" className="gap-1" onClick={() => showResults(ann.id)} data-testid={`results-${ann.id}`}>
                              <BarChart3 className="w-3 h-3" /> Sonuçlar
                            </Button>
                            <Button variant="outline" size="sm" className="gap-1" onClick={() => handleRemind(ann.id)} data-testid={`remind-${ann.id}`}>
                              <Bell className="w-3 h-3" /> Hatırlat
                            </Button>
                            <Button variant="outline" size="sm" className="gap-1 text-amber-600 border-amber-200" onClick={() => handleCloseVoting(ann.id)}>
                              Oylamayı Kapat
                            </Button>
                          </>
                        )}
                        {isVotable && (
                          <Button variant="outline" size="sm" className="gap-1 text-[#2563EB] border-[#2563EB]/30" onClick={() => downloadPdf(ann.id, ann.title)} data-testid={`pdf-${ann.id}`}>
                            <Download className="w-3 h-3" /> PDF Rapor
                          </Button>
                        )}
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp"
                            onChange={(e) => { if (e.target.files[0]) handleFileUpload(ann.id, e.target.files[0]); e.target.value = ''; }} />
                          <Button variant="outline" size="sm" className="gap-1" asChild><span><Upload className="w-3 h-3" /> Belge</span></Button>
                        </label>
                        <Button variant="outline" size="sm" className="text-red-500 border-red-200 gap-1" onClick={() => handleDelete(ann.id)} data-testid={`delete-ann-${ann.id}`}>
                          <Trash2 className="w-3 h-3" /> Sil
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Results Dialog */}
        <Dialog open={!!resultDialog} onOpenChange={(open) => { if (!open) { setResultDialog(null); setResultData(null); } }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Oylama Sonuçları</DialogTitle>
            </DialogHeader>
            {resultData && (
              <div className="space-y-4" data-testid="results-dialog">
                {/* Participation */}
                <Card className="border border-[#E5E5E5]">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-2xl font-bold text-[#0F172A]">{resultData.total_votes}</p>
                        <p className="text-xs text-[#595959]">Oy Kullanan</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#1A1A2E]">{resultData.total_users}</p>
                        <p className="text-xs text-[#595959]">Toplam Sakin</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-600">%{resultData.participation_rate}</p>
                        <p className="text-xs text-[#595959]">Katılım</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Vote Breakdown */}
                {resultData.breakdown && (
                  <Card className="border border-[#E5E5E5]">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Oy Dağılımı</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                      {Object.entries(resultData.breakdown).map(([key, count]) => {
                        const pct = resultData.total_votes > 0 ? (count / resultData.total_votes * 100) : 0;
                        const colorMap = { evet: 'bg-green-500', hayir: 'bg-red-500', cekimser: 'bg-gray-400' };
                        return (
                          <div key={key} className="flex items-center gap-3">
                            <span className="text-sm w-20 capitalize">{key === 'evet' ? 'Evet' : key === 'hayir' ? 'Hayır' : key === 'cekimser' ? 'Çekimser' : key}</span>
                            <div className="flex-1 bg-[#F0F0F0] rounded-full h-4 overflow-hidden">
                              <div className={`h-full rounded-full ${colorMap[key] || 'bg-[#2563EB]'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-sm font-medium w-16 text-right">{count} (%{Math.round(pct)})</span>
                          </div>
                        );
                      })}
                      {resultData.majority && (
                        <p className="text-sm font-medium text-[#2563EB] mt-2">
                          Sonuç: {resultData.majority === 'evet' ? 'Evet çoğunlukta' : resultData.majority === 'hayir' ? 'Hayır çoğunlukta' : 'Eşit'}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Non-voters */}
                {resultData.non_voters && resultData.non_voters.length > 0 && (
                  <Card className="border border-[#E5E5E5]">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-red-600">Oy Kullanmayanlar ({resultData.non_voters.length})</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {resultData.non_voters.map(nv => (
                          <div key={nv.user_id} className="flex items-center justify-between text-sm py-1 border-b border-[#F0F0F0] last:border-0">
                            <span>{nv.name}</span>
                            <span className="text-xs text-[#595959]">{nv.apartment_number || nv.email}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Voters */}
                {resultData.voters && resultData.voters.length > 0 && (
                  <Card className="border border-[#E5E5E5]">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Oy Kullananlar ({resultData.voters.length})</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {resultData.voters.map(v => (
                          <div key={v.user_id} className="flex items-center justify-between text-sm py-1 border-b border-[#F0F0F0] last:border-0">
                            <span>{v.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${v.vote === 'evet' ? 'bg-green-100 text-green-700' : v.vote === 'hayir' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                              {v.vote === 'evet' ? 'Evet' : v.vote === 'hayir' ? 'Hayır' : v.vote === 'cekimser' ? 'Çekimser' : v.vote}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* PDF Download Button */}
                <Button className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] gap-2 mt-2" onClick={() => { downloadPdf(resultDialog, announcements.find(a => a.id === resultDialog)?.title || 'rapor'); }} data-testid="pdf-download-btn">
                  <Download className="w-4 h-4" /> PDF Olarak İndir (Karar Tutanağı)
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PageLayout>
  );
};
